import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useState } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import PortalIdParser from 'PortalIdParser';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import formatName from 'I18n/utils/formatName';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import I18n from 'I18n';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from '../../../app/auth/AuthSelectors';
import { addErrorAlert, addSuccessAlert } from '../../../app/utils/alerting';
import { trackBetaEnrollmentChange } from '../../../app/utils/tracking';
import { EnrollmentStatuses } from '../../constants';
import OneOfReasonUITooltip from '../utils/OneOfReasonUITooltip';
import useUpdateBetaEnrollment from '../../hooks/useUpdateBetaEnrollment';
import { updateProductUpdateCache } from '../../queries';
import useFetchOrgData from '../../hooks/useFetchOrgData';
import { getIsOptionDisabled } from '../utils/enrollmentStatus';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const EditBetaDialog = ({
  id,
  rolloutId,
  betaAccessType,
  enrollmentStatus,
  enrollType,
  title,
  untranslatedTitle,
  gate,
  isSlowRollout = false,
  onClose
}) => {
  const portalId = PortalIdParser.get();
  const user = useSelector(selectAuthenticatedUser);
  const [desiredState, setDesiredState] = useState(enrollmentStatus);
  const userId = user && user.user_id;
  const {
    isInOrg,
    portalsInOrgCount
  } = useFetchOrgData();
  const isPortalOptionDisabled = getIsOptionDisabled('PORTAL', enrollType);
  const isUserOptionDisabled = getIsOptionDisabled('USER', enrollType);
  const isOrgOptionDisabled = getIsOptionDisabled('ORGANIZATION', enrollType);
  const [updateBetaEnrollment, {
    loading,
    error
  }] = useUpdateBetaEnrollment({
    betaAccessType,
    desiredState,
    portalId: portalId !== null && portalId !== void 0 ? portalId : 0,
    rolloutId,
    userId: userId !== null && userId !== void 0 ? userId : 0
  }, {
    onCompleted: () => {
      trackBetaEnrollmentChange({
        title,
        untranslatedTitle,
        gateName: gate,
        currentState: enrollmentStatus,
        desiredState
      });
      addSuccessAlert(desiredState === EnrollmentStatuses.USER ? 'app.betas.success.youJoined' : 'app.betas.success.accountJoined', title);
      onClose();
    },
    onError: () => {
      addErrorAlert('app.betas.errors.problemUpdating');
      onClose();
    },
    update: cache => {
      updateProductUpdateCache(cache, {
        productUpdateId: `${id}`,
        rolloutId: `${rolloutId}`,
        nextEnrollmentStatus: desiredState,
        approvedAt: new Date().valueOf()
      });
    }
  });
  const handleEdit = useCallback(() => {
    if (!portalId || !userId) {
      addErrorAlert(desiredState === EnrollmentStatuses.USER ? 'app.betas.errors.problemJoiningYou' : 'app.betas.errors.problemJoiningAccount');
      return;
    }
    void updateBetaEnrollment();
  }, [desiredState, portalId, userId, updateBetaEnrollment]);
  if (!user) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Modal, {
    use: "success",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betas.edit.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(Paragraph, {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: betaAccessType === 'PUBLIC' ? 'app.betas.edit.publicBetaContent' : 'app.betas.edit.privateBetaContent',
          options: {
            betaName: title
          }
        })
      }), /*#__PURE__*/_jsxs(ToggleGroup, {
        name: "beta-target",
        marginBlockStart: "600",
        children: [/*#__PURE__*/_jsx(OneOfReasonUITooltip, {
          placement: "bottom",
          oneOf: [{
            showWhen: isUserOptionDisabled,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.disabledOption"
            })
          }, {
            showWhen: isSlowRollout,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.disabledOptionSlowRollout"
            })
          }],
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-user-radio",
            checked: desiredState === EnrollmentStatuses.USER,
            disabled: isUserOptionDisabled || isSlowRollout,
            onChange: () => setDesiredState('USER'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.edit.adminOption",
              options: {
                name: formatName({
                  givenName: user.first_name,
                  familyName: user.last_name,
                  email: user.email
                })
              }
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isPortalOptionDisabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.join.disabledOption"
          }),
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-portal-radio",
            checked: desiredState === EnrollmentStatuses.PORTAL,
            disabled: isPortalOptionDisabled,
            onChange: () => setDesiredState('PORTAL'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.edit.accountOption",
              options: {
                portalId: I18n.SafeString(PortalIdParser.get().toString())
              }
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: !isOrgOptionDisabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.join.disabledOption"
          }),
          children: /*#__PURE__*/_jsx(RadioInput, {
            checked: desiredState === EnrollmentStatuses.ORGANIZATION,
            disabled: isOrgOptionDisabled,
            onChange: () => setDesiredState('ORGANIZATION'),
            children: enrollType === 'ORGANIZATIONS_ONLY' && isInOrg ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.organizationOption",
              options: {
                portalsInOrg: I18n.SafeString(portalsInOrgCount.toString())
              }
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betas.join.organizationOptionDefault"
            })
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        disabled: desiredState !== enrollmentStatus,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betas.edit.disabled"
        }),
        children: /*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": "edit-beta-modal-accept-btn",
          use: "primary",
          failed: Boolean(error),
          loading: loading,
          onClick: handleEdit,
          disabled: desiredState === enrollmentStatus,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.edit.action"
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betas.edit.cancel"
        })
      })]
    })]
  });
};
export default EditBetaDialog;